import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '@/services/api'
import { Card, Button, Input, Select, Table, Pagination, Badge, ConfidenceBadge, DetectionAlertCell } from '@/components/ui'
import { DEFAULT_PAGE_SIZE } from '@spoofio/shared'
import { formatListTimestamp } from '@/utils/format-date'
import type { DetectionResponse, DetectionQueryParams } from '@spoofio/shared/types'

export function DetectionListPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Get filters from URL
  const canaryIdParam = searchParams.get('canaryId') ?? ''
  const minConfidenceParam = searchParams.get('minConfidence') ?? ''
  const fakeFqdnParam = searchParams.get('fakeFqdn') ?? ''
  const pageParam = parseInt(searchParams.get('page') ?? '1', 10)

  const [filters, setFilters] = useState<DetectionQueryParams>({
    canaryId: canaryIdParam || undefined,
    minConfidence: minConfidenceParam ? parseInt(minConfidenceParam, 10) : undefined,
    fakeFqdn: fakeFqdnParam || undefined,
    limit: DEFAULT_PAGE_SIZE,
    offset: (pageParam - 1) * DEFAULT_PAGE_SIZE,
    sortBy: 'detected_at',
    sortOrder: 'desc',
  })

  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.listOrganizations(),
  })

  const orgId = organizations?.[0]?.id

  const { data: canaries } = useQuery({
    queryKey: ['canaries', orgId],
    queryFn: () => (orgId ? api.listCanaries(orgId) : Promise.resolve([])),
    enabled: !!orgId,
  })

  const { data: detectionsData, isLoading, error } = useQuery({
    queryKey: ['detections', orgId, filters],
    queryFn: () =>
      api.listDetections({
        ...filters,
        organizationId: orgId,
      }),
    enabled: !!orgId,
  })

  const handleFilterChange = (key: keyof DetectionQueryParams, value: string | number | undefined) => {
    const newFilters = {
      ...filters,
      [key]: value ?? undefined,
      offset: 0, // Reset to first page on filter change
    }
    setFilters(newFilters)

    // Update URL params
    const newParams = new URLSearchParams()
    if (newFilters.canaryId) newParams.set('canaryId', newFilters.canaryId)
    if (newFilters.minConfidence) newParams.set('minConfidence', newFilters.minConfidence.toString())
    if (newFilters.fakeFqdn) newParams.set('fakeFqdn', newFilters.fakeFqdn)
    newParams.set('page', '1')
    setSearchParams(newParams)
  }

  const handlePageChange = (page: number) => {
    const newFilters = {
      ...filters,
      offset: (page - 1) * DEFAULT_PAGE_SIZE,
    }
    setFilters(newFilters)

    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', page.toString())
    setSearchParams(newParams)
  }

  const handleExportCsv = () => {
    if (!detectionsData?.data) return

    // SECURITY: Escape CSV values to prevent formula injection
    // Cells starting with =, +, -, @, \t, \r could be interpreted as formulas
    const escapeCsvCell = (value: string | number | boolean): string => {
      const str = String(value)
      const escaped = str.replace(/"/g, '""')
      // Prefix with single quote if starts with formula characters
      if (/^[=+\-@\t\r]/.test(escaped)) {
        return `"'${escaped}"`
      }
      return `"${escaped}"`
    }

    const headers = [
      'ID',
      'Detected At',
      'Fake Domain',
      'Canary',
      'Confidence Score',
      'Visitor IP',
      'Country',
      'City',
      'User Agent',
      'Referrer',
      'Alert Sent',
      'Detection Method',
      'Source Method',
    ]

    const rows = detectionsData.data.map((d) => [
      d.id,
      d.detectedAt,
      d.fakeFqdn,
      d.canaryName,
      d.confidenceScore,
      d.visitorIp,
      d.cfCountry ?? '',
      d.cfCity ?? '',
      d.userAgent ?? '',
      d.referrer ?? '',
      d.alertSent ? 'Yes' : 'No',
      d.detectionMethod ?? 'pixel',
      d.sourceMethod ?? 'referer',
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map(escapeCsvCell).join(',')),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `detections-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  const columns = [
    {
      key: 'fakeFqdn',
      header: 'Fake Domain',
      render: (d: DetectionResponse) => (
        <div>
          <span className="font-mono text-sm">{d.fakeFqdn}</span>
          {d.fakeUrl && (
            <p className="text-xs text-gray-400 truncate max-w-xs">{d.fakeUrl}</p>
          )}
        </div>
      ),
    },
    {
      key: 'canaryName',
      header: 'Canary',
      render: (d: DetectionResponse) => (
        <span className="text-primary-600">{d.canaryName}</span>
      ),
    },
    {
      key: 'confidenceScore',
      header: 'Confidence',
      render: (d: DetectionResponse) => <ConfidenceBadge score={d.confidenceScore} />,
    },
    {
      key: 'location',
      header: 'Location',
      render: (d: DetectionResponse) =>
        d.cfCountry ? (
          <span>{d.cfCity ? `${d.cfCity}, ` : ''}{d.cfCountry}</span>
        ) : (
          <span className="text-gray-400">Unknown</span>
        ),
    },
    {
      key: 'detectionMethod',
      header: 'Method',
      render: (d: DetectionResponse) => (
        <Badge variant={d.detectionMethod === 'pixel' ? 'gray' : 'primary'}>
          {d.detectionMethod === 'css' ? 'CSS' :
           d.detectionMethod === 'js' ? 'JS' :
           'Pixel'}
        </Badge>
      ),
    },
    {
      key: 'detectedAt',
      header: 'Time',
      render: (d: DetectionResponse) => formatListTimestamp(d.detectedAt),
    },
    {
      key: 'alertSent',
      header: 'Alert',
      render: (d: DetectionResponse) => (
        <DetectionAlertCell alertSent={d.alertSent} screenshotUrl={d.screenshotUrl} />
      ),
    },
  ]

  const totalPages = detectionsData
    ? Math.ceil(detectionsData.total / DEFAULT_PAGE_SIZE)
    : 1
  const currentPage = Math.floor((filters.offset ?? 0) / DEFAULT_PAGE_SIZE) + 1

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Detections</h1>
        <Button variant="secondary" onClick={handleExportCsv} disabled={!detectionsData?.data.length}>
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-4">
          <Select
            label="Canary"
            value={filters.canaryId ?? ''}
            onChange={(e) => { handleFilterChange('canaryId', e.target.value); }}
            options={[
              { value: '', label: 'All Canaries' },
              ...(canaries?.map((c) => ({ value: c.id, label: c.name })) ?? []),
            ]}
            className="w-48"
          />

          <div className="w-40">
            <label className="label">Min Confidence</label>
            <input
              type="range"
              min="0"
              max="100"
              value={filters.minConfidence ?? 0}
              onChange={(e) =>
                { handleFilterChange(
                  'minConfidence',
                  e.target.value === '0' ? undefined : parseInt(e.target.value, 10)
                ); }
              }
              className="w-full"
            />
            <span className="text-sm text-gray-500">
              {filters.minConfidence ?? 0}%+
            </span>
          </div>

          <Input
            label="Fake Domain"
            placeholder="Search domain..."
            value={filters.fakeFqdn ?? ''}
            onChange={(e) => { handleFilterChange('fakeFqdn', e.target.value); }}
            className="w-48"
          />

          <div className="flex items-end">
            <Button
              variant="ghost"
              onClick={() => {
                setFilters({
                  limit: DEFAULT_PAGE_SIZE,
                  offset: 0,
                  sortBy: 'detected_at',
                  sortOrder: 'desc',
                })
                setSearchParams(new URLSearchParams())
              }}
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Results */}
      {error ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-danger-600">Failed to load detections</p>
          </div>
        </Card>
      ) : (
        <Card>
          <Table
            columns={columns}
            data={detectionsData?.data ?? []}
            keyExtractor={(d) => d.id}
            onRowClick={(d) => { navigate(`/detections/${d.id}`); }}
            isLoading={isLoading}
            emptyMessage="No detections found matching your filters"
          />
          {detectionsData && detectionsData.total > DEFAULT_PAGE_SIZE && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={detectionsData.total}
              itemsPerPage={DEFAULT_PAGE_SIZE}
              onPageChange={handlePageChange}
            />
          )}
        </Card>
      )}
    </div>
  )
}

